import type { GameStateData } from '../../game/GameState.js';
import type { AiAction } from '../types.js';
import type { WorkerRequest, WorkerResponse } from './protocol.js';

/**
 * Main-thread bridge to the Hard-tier ISMCTS Web Worker. Exposes an async
 * `search()` returning the chosen action; supports cancellation (used when
 * the AI seat changes mid-think or the player navigates back to the menu).
 *
 * Constructed lazily — `tryCreate()` returns null in environments without
 * Worker support (SSR, some test runners). Callers fall back to the in-
 * process searcher in that case.
 */
export interface SearchOptions {
  readonly iterations: number;
  readonly deadlineMs: number;
}

export interface SearchResult {
  readonly action: AiAction;
  readonly iterations: number;
}

export class HardWorkerSearcher {
  private nextId = 0;
  private readonly pending = new Map<string, (res: SearchResult | Error) => void>();

  constructor(private readonly worker: Worker) {
    this.worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const res = e.data;
      const cb = this.pending.get(res.id);
      if (!cb) return;
      this.pending.delete(res.id);
      if (res.kind === 'result') {
        cb({ action: res.action, iterations: res.iterations });
      } else {
        cb(new Error(res.message));
      }
    };
  }

  static tryCreate(): HardWorkerSearcher | null {
    if (typeof Worker === 'undefined') return null;
    try {
      const worker = new Worker(new URL('./dominoAi.worker.ts', import.meta.url), {
        type: 'module',
      });
      return new HardWorkerSearcher(worker);
    } catch {
      return null;
    }
  }

  search(
    state: GameStateData,
    seat: number,
    options: SearchOptions,
  ): { promise: Promise<SearchResult>; cancel: () => void } {
    const id = `s${this.nextId++}`;
    const promise = new Promise<SearchResult>((resolve, reject) => {
      this.pending.set(id, (out) => {
        if (out instanceof Error) reject(out);
        else resolve(out);
      });
      const req: WorkerRequest = {
        kind: 'search',
        id,
        state,
        seat,
        iterations: options.iterations,
        deadlineMs: options.deadlineMs,
      };
      this.worker.postMessage(req);
    });
    const cancel = (): void => {
      const req: WorkerRequest = { kind: 'cancel', id };
      this.worker.postMessage(req);
    };
    return { promise, cancel };
  }

  dispose(): void {
    this.pending.clear();
    this.worker.terminate();
  }
}
